import { firestore } from "../firebase/config";
import { getAuth, signInAnonymously } from "firebase/auth";
import {
  collection,
  addDoc,
  updateDoc,
  doc,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  getDoc,
  serverTimestamp,
} from "firebase/firestore";

const COLLECTION_NAME = "websitePostHistory";

// Helper function để đảm bảo user được authenticate
const ensureAuthenticated = async () => {
  const auth = getAuth();

  if (!auth.currentUser) {
    console.log("🔐 No authenticated user, signing in anonymously...");
    try {
      const result = await signInAnonymously(auth);
      console.log("✅ Anonymous authentication successful:", result.user.uid);
      return result.user;
    } catch (error) {
      console.error("❌ Anonymous authentication failed:", error);
      throw error;
    }
  }

  console.log("✅ User already authenticated:", auth.currentUser.uid);
  return auth.currentUser;
};

export const postHistoryService = {
  // Tạo lịch sử mới
  async createHistory(historyData) {
    console.log("🔧 postHistoryService.createHistory called");
    console.log("📋 Input data:", historyData);
    console.log("🔥 Firestore instance:", firestore);
    console.log("📁 Collection name:", COLLECTION_NAME);

    try {
      const dataToSave = {
        ...historyData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      console.log("💾 Data to save:", dataToSave);
      console.log("📤 Attempting to add document to Firestore...");

      const docRef = await addDoc(
        collection(firestore, COLLECTION_NAME),
        dataToSave
      );

      console.log("✅ Document added successfully with ID:", docRef.id);
      return { success: true, id: docRef.id };
    } catch (error) {
      console.error("❌ Error creating post history:", error);
      console.error("❌ Error code:", error.code);
      console.error("❌ Error message:", error.message);
      console.error("❌ Error stack:", error.stack);

      // Additional debugging info
      if (error.code) {
        console.error("🔍 Firebase error code analysis:");
        switch (error.code) {
          case "permission-denied":
            console.error("🚫 Permission denied - check Firestore rules");
            break;
          case "unavailable":
            console.error(
              "📡 Firestore unavailable - check network connection"
            );
            break;
          case "unauthenticated":
            console.error("🔐 User not authenticated");
            break;
          default:
            console.error("❓ Unknown Firebase error code:", error.code);
        }
      }

      return { success: false, error: error.message, code: error.code };
    }
  },

  // Cập nhật lịch sử
  async updateHistory(historyId, updateData) {
    try {
      const docRef = doc(firestore, COLLECTION_NAME, historyId);
      await updateDoc(docRef, {
        ...updateData,
        updatedAt: serverTimestamp(),
      });
      return { success: true };
    } catch (error) {
      console.error("Error updating post history:", error);
      return { success: false, error: error.message };
    }
  },

  // Lấy lịch sử theo userId
  async getUserHistory(userId, limitCount = 50) {
    try {
      const q = query(
        collection(firestore, COLLECTION_NAME),
        where("userId", "==", userId),
        orderBy("createdAt", "desc"),
        limit(limitCount)
      );

      const querySnapshot = await getDocs(q);
      const histories = [];
      querySnapshot.forEach((doc) => {
        histories.push({ id: doc.id, ...doc.data() });
      });

      return { success: true, data: histories };
    } catch (error) {
      console.error("Error getting user history:", error);
      return { success: false, error: error.message };
    }
  },

  // Lấy tất cả lịch sử (cho admin)
  async getAllHistory(limitCount = 100) {
    try {
      // Đảm bảo user được authenticate trước khi truy cập Firestore
      await ensureAuthenticated();

      const q = query(
        collection(firestore, COLLECTION_NAME),
        orderBy("createdAt", "desc"),
        limit(limitCount)
      );

      const querySnapshot = await getDocs(q);
      const histories = [];
      querySnapshot.forEach((doc) => {
        histories.push({ id: doc.id, ...doc.data() });
      });

      console.log(
        `✅ Successfully retrieved ${histories.length} history records`
      );
      return { success: true, data: histories };
    } catch (error) {
      console.error("Error getting all history:", error);
      return { success: false, error: error.message };
    }
  },

  // Lấy thống kê
  async getHistoryStats(userId = null) {
    try {
      // Đảm bảo user được authenticate trước khi truy cập Firestore
      await ensureAuthenticated();

      let q;
      if (userId) {
        q = query(
          collection(firestore, COLLECTION_NAME),
          where("userId", "==", userId)
        );
      } else {
        q = query(collection(firestore, COLLECTION_NAME));
      }

      const querySnapshot = await getDocs(q);
      const stats = {
        total: 0,
        success: 0,
        failed: 0,
        totalPointsUsed: 0,
        totalFreeUsed: 0,
      };

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        stats.total++;

        if (data.status === "success") stats.success++;
        if (data.status === "failed") stats.failed++;

        if (data.pointsData?.pointsDeductedAmount) {
          stats.totalPointsUsed += data.pointsData.pointsDeductedAmount;
        }

        if (data.pointsData?.usedFreeQuantity) {
          stats.totalFreeUsed++;
        }
      });

      console.log(
        `✅ Successfully calculated stats for ${stats.total} records`
      );
      return { success: true, data: stats };
    } catch (error) {
      console.error("Error getting history stats:", error);
      return { success: false, error: error.message };
    }
  },
};
